/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRiskAnalysisStatusRequest,
  GetRiskAnalysisStatusSecurity,
} from "../models/operations/getriskanalysisstatus.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskAnalysisStatusQuery,
  prefetchRiskAnalysisStatus,
  queryKeyRiskAnalysisStatus,
  RiskAnalysisStatusQueryData,
} from "./riskAnalysisStatus.core.js";
export {
  buildRiskAnalysisStatusQuery,
  prefetchRiskAnalysisStatus,
  queryKeyRiskAnalysisStatus,
  type RiskAnalysisStatusQueryData,
};

export type RiskAnalysisStatusQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRiskAnalysisStatus risk
 *
 * @remarks
 * Get the run state of the project's risk analysis coordinator, which produces the findings behind the Watchdog page. Analysis is signal-driven, not scheduled: the coordinator wakes within about 30 seconds of new chat traffic and never runs on a timer, so a quiet project can legitimately go a long time between runs. The state is never when no run is visible (the project has never been analyzed, or its last run is older than Temporal's retention window), idle when the latest run has closed and the coordinator is waiting for the next chat write, and running when a run is in flight.
 */
export function useRiskAnalysisStatus(
  request?: GetRiskAnalysisStatusRequest | undefined,
  security?: GetRiskAnalysisStatusSecurity | undefined,
  options?: QueryHookOptions<
    RiskAnalysisStatusQueryData,
    RiskAnalysisStatusQueryError
  >,
): UseQueryResult<RiskAnalysisStatusQueryData, RiskAnalysisStatusQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskAnalysisStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRiskAnalysisStatus risk
 *
 * @remarks
 * Get the run state of the project's risk analysis coordinator, which produces the findings behind the Watchdog page. Analysis is signal-driven, not scheduled: the coordinator wakes within about 30 seconds of new chat traffic and never runs on a timer, so a quiet project can legitimately go a long time between runs. The state is never when no run is visible (the project has never been analyzed, or its last run is older than Temporal's retention window), idle when the latest run has closed and the coordinator is waiting for the next chat write, and running when a run is in flight.
 */
export function useRiskAnalysisStatusSuspense(
  request?: GetRiskAnalysisStatusRequest | undefined,
  security?: GetRiskAnalysisStatusSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskAnalysisStatusQueryData,
    RiskAnalysisStatusQueryError
  >,
): UseSuspenseQueryResult<
  RiskAnalysisStatusQueryData,
  RiskAnalysisStatusQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskAnalysisStatusQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskAnalysisStatusData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskAnalysisStatusQueryData,
): RiskAnalysisStatusQueryData | undefined {
  const key = queryKeyRiskAnalysisStatus(...queryKeyBase);

  return client.setQueryData<RiskAnalysisStatusQueryData>(key, data);
}

export function invalidateRiskAnalysisStatus(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "signals", "analysisStatus", ...queryKeyBase],
  });
}

export function invalidateAllRiskAnalysisStatus(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "signals", "analysisStatus"],
  });
}
